// Copyright 1999-2017. Plesk International GmbH. All rights reserved.

import { render, registerComponent } from './common';
import { Tooltip } from './tooltip';
import { Locale } from './locale';

/**
 * @class Jsw.Component
 * The base class for all Jsw components.
 */
export const Component = Class.create({
    _tag: 'div',

    /**
     * @cfg {String} id The unique id of this component instance.
     */
    /**
     * @cfg {String} cls CSS class names to apply for root element.
     */
    /**
     * @cfg {Object} attrs HTML attributes to apply for root element.
     */
    /**
     * @cfg {String} wrapperClass
     */
    /**
     * @cfg {Element|String} renderTo
     */
    /**
     * @cfg {String} renderMode=bottom
     */
    /**
     * @cfg {Element|String} applyTo
     */
    /**
     * @cfg {Object} locale
     */
    /**
     * @cfg {Boolean} hidden=false Hide component. See methods {@link #hide}/{@link #show}
     */

    initialize(config) {
        this._initConfiguration(config);
        this._registerComponent();

        this._initComponentElement();

        if (this._autoRender && ((null !== this._renderTarget) || (null !== $(this._applyTargetId)))) {
            this.render();
        }

        if (this._getConfigParam('hidden', false)) {
            this.hide();
        }
    },

    _initConfiguration(config) {
        this.initialConfig = config;
        this._config = this.initialConfig;

        this._cls = this._getConfigParam('cls', '');
        this._attrs = this._getConfigParam('attrs', {});
        this._wrapperClass = this._getConfigParam('wrapperClass', '');
        this._autoRender = this._getConfigParam('autoRender', true);
        this._renderTarget = $(this._getConfigParam('renderTo', null));
        this._renderMode = this._getConfigParam('renderMode', 'bottom');
        this._applyTargetId = this._getConfigParam('applyTo', null);
        this.setLocale(this._getConfigParam('locale'));

        this._id = (this._applyTargetId)
            ? this._applyTargetId
            : this._getConfigParam('id', null);

        if (null === this._id) {
            this._id = this._getRandomId();
            this._idIsRandom = true;
        }

        this._tag = (this._applyTargetId)
            ? $(this._applyTargetId).tagName
            : this._getConfigParam('tag', this._tag);
    },

    _registerComponent() {
        registerComponent(this._id, this);
    },

    _initComponentElement() {
        if (this._applyTargetId) {
            this._componentElement = $(this._applyTargetId);
            return;
        }

        const attrs = Object.extend(this._attrs, {
            id: this._id,
            class: this._cls,
        });

        this._componentElement = new Element(this._tag, attrs);
    },

    _initDisablerOverlay() {
        this._disablerOverlay = new Element('div');

        this._disablerOverlay.setStyle({
            display: 'none',
            zIndex: this._componentElement.style.zIndex + 1000,
        });

        this._disablerOverlay.addClassName('content-blocker');
        this._componentElement.appendChild(this._disablerOverlay);
    },

    addEventObserver(eventName, handler) {
        if ('undefined' !== typeof handler) {
            this._componentElement.observe(eventName, handler);
        }
    },

    removeEventObserver(eventName, handler) {
        if ('undefined' !== typeof handler) {
            this._componentElement.stopObserving(eventName, handler);
        }
    },

    _hasConfigParam(name) {
        return 'undefined' !== typeof this._config[name];
    },

    _getConfigParam(name, defaultValue) {
        return this._hasConfigParam(name)
            ? this._config[name]
            : defaultValue;
    },

    _getRandomId() {
        let id = `gen-id-${this._getRandomNumber()}`;

        while (null !== $(id)) {
            id = `gen-id-${this._getRandomNumber()}`;
        }

        return id;
    },

    _getRandomNumber() {
        return Math.floor(1000000 * Math.random());
    },

    _addEvents() {
    },

    _addTooltips() {
        const description = this._getConfigParam('description');
        if (description) {
            this._tooltip = Tooltip.init(this._componentElement, { text: description });
        }
    },

    _updateComponentElement(content) {
        if (this._componentElement.empty()) {
            this._componentElement.update(content);
            return;
        }

        const oldElements = this._componentElement.childElements();

        Element.insert(this._componentElement.firstChild, { before: content });

        oldElements.each(function (oldElement) {
            if ('undefined' === typeof oldElement.id || '' == oldElement.id) {
                return;
            }

            const newElement = this._componentElement.select(`#${oldElement.id}`).first();

            if (newElement) {
                newElement.replace(oldElement);
            }
        }, this);
    },

    /**
     * See {@link Jsw.Component}.{@link Jsw.Component#wrapperClass wrapperClass} for details.
     * @returns {String}
     */
    getWrapperClass() {
        return this._wrapperClass;
    },

    /**
     * Returns id of this component instance.
     * @returns {String}
     */
    getId() {
        return this._id;
    },

    /**
     * Set render target element.
     * @param {Element} target
     */
    setRenderTarget(target) {
        this._renderTarget = target;
    },

    /**
     * Get render target element.
     * @returns {Element}
     */
    getRenderTarget() {
        return this._renderTarget;
    },

    /**
     * Show component.
     */
    show() {
        this._componentElement.show();
    },

    /**
     * Hide component.
     */
    hide() {
        this._componentElement.hide();
    },

    /**
     * Render component.
     */
    render() {
        if (this._renderTarget) {
            render(this._renderTarget, this._componentElement, this._renderMode);
        }

        this._addEvents();
        this._addTooltips();
    },

    setLocale(locale) {
        if (!(locale instanceof Locale)) {
            locale = new Locale(locale);
        }
        this.lmsg = locale.lmsg.bind(locale);
        this._locale = locale;
    },

    getLocale() {
        return this._locale;
    },
});



// WEBPACK FOOTER //
// ./jsw/component.js